import { dehydrate, hydrate, QueryClient, type Query } from "@tanstack/react-query";
import { openDB } from "idb";
import type { ReviewView as ReviewViewSchema, SearchResult as SearchResultSchema, TaskPage, TaskWithFlags, TodayView, UpcomingView as UpcomingViewSchema } from "@desk/contracts";
import type { z } from "zod";

type UpcomingView = z.infer<typeof UpcomingViewSchema>;
type ReviewView = z.infer<typeof ReviewViewSchema>;
type SearchResult = z.infer<typeof SearchResultSchema>;

export const qk = {
  bootstrap: ["bootstrap"] as const,
  tasks: (q: unknown) => ["tasks", q] as const,
  task: (id: string) => ["task", id] as const,
  today: (date: string, tz: string) => ["today", date, tz] as const,
  upcoming: (from: string) => ["upcoming", from] as const,
  review: ["review"] as const,
  snoozed: ["snoozed"] as const,
  settled: ["settled"] as const,
  search: (q: string) => ["search", q] as const,
  settings: ["settings"] as const,
  mcp: ["mcp"] as const,
  archived: ["archived"] as const,
  cloud: ["cloud"] as const,
  sharing: ["sharing"] as const,
};

const TASK_ROOTS = new Set(["tasks", "task", "today", "upcoming", "review", "snoozed", "search"]);
const CONTAINER_ROOTS = new Set(["bootstrap", "settled", "archived"]);

export function createQueryClient() {
  return new QueryClient({
    defaultOptions: {
      queries: {
        staleTime: 15_000,
        gcTime: 24 * 60 * 60_000,
        retry: (count, err) => {
          const status = (err as { status?: number }).status ?? 0;
          if (status >= 400 && status < 500) return false;
          return count < 2;
        },
        refetchOnWindowFocus: true,
        networkMode: "offlineFirst",
      },
      mutations: { networkMode: "always" },
    },
  });
}

export function invalidateTaskData(qc: QueryClient) {
  return qc.invalidateQueries({ predicate: (q) => TASK_ROOTS.has(String(q.queryKey[0])) });
}

export function invalidateContainers(qc: QueryClient) {
  return qc.invalidateQueries({ predicate: (q) => CONTAINER_ROOTS.has(String(q.queryKey[0])) || TASK_ROOTS.has(String(q.queryKey[0])) });
}

/** Replace a task in every cached list/detail/view where it appears (keeps UI steady before refetch). */
export function upsertTaskInCaches(qc: QueryClient, task: TaskWithFlags) {
  const merge = (t: TaskWithFlags): TaskWithFlags => (t.id === task.id ? { ...t, ...task, flags: task.flags ?? t.flags } : t);
  const rows = <R extends { task: TaskWithFlags }>(r: R[]) => r.map((row) => ({ ...row, task: merge(row.task) }));
  qc.setQueriesData<TaskPage>({ queryKey: ["tasks"] }, (page) => (page ? { ...page, items: page.items.map(merge) } : page));
  qc.setQueriesData<TaskWithFlags>({ queryKey: ["task"] }, (t) => (t && t.id === task.id ? merge(t) : t));
  qc.setQueriesData<TodayView>({ queryKey: ["today"] }, (v) =>
    v ? { ...v, planned: rows(v.planned), carryover: rows(v.carryover), due: rows(v.due), completed: rows(v.completed) } : v,
  );
  qc.setQueriesData<UpcomingView>({ queryKey: ["upcoming"] }, (v) =>
    v ? { ...v, days: v.days.map((d) => ({ ...d, planned: d.planned.map(merge), deadlines: d.deadlines.map(merge) })) } : v,
  );
  qc.setQueriesData<ReviewView>({ queryKey: ["review"] }, (v) => (v ? { ...v, items: rows(v.items) } : v));
  qc.setQueriesData<TaskWithFlags[]>({ queryKey: ["snoozed"] }, (v) => (Array.isArray(v) ? v.map(merge) : v));
  qc.setQueriesData<SearchResult>({ queryKey: ["search"] }, (v) => (v ? { ...v, tasks: v.tasks.map(merge) } : v));
}

/**
 * Debounced invalidation used by the live event stream. A steady burst of events (an import, an
 * agent editing many tasks) still flushes at least every `maxWait` ms instead of waiting for quiet.
 */
export function createEventInvalidator(qc: QueryClient, delay = 200, maxWait = 1000) {
  let timer: ReturnType<typeof setTimeout> | null = null;
  let first = 0;
  let tasks = false;
  let containers = false;
  let settings = false;
  const flush = () => {
    timer = null;
    first = 0;
    if (containers) void invalidateContainers(qc);
    else if (tasks) void invalidateTaskData(qc);
    if (containers || settings) void qc.invalidateQueries({ queryKey: ["settings"] });
    tasks = false;
    containers = false;
    settings = false;
  };
  return (entity: string | undefined) => {
    if (entity === "task" || entity === "plan") tasks = true;
    else containers = true;
    if (entity === "settings") settings = true;
    const now = Date.now();
    if (!first) first = now;
    if (timer) clearTimeout(timer);
    timer = setTimeout(flush, Math.max(0, Math.min(delay, first + maxWait - now)));
  };
}

// ---------- IndexedDB cache persistence (read-only offline view) ----------
const CACHE_KEY = "queries";
const PERSIST_ROOTS = new Set(["bootstrap", "tasks", "today", "upcoming", "review", "snoozed", "settings", "settled"]);
const cacheDb = () =>
  openDB("desk-cache", 1, {
    upgrade(db) {
      db.createObjectStore("kv");
    },
  });

export async function restoreCache(qc: QueryClient, scope: string) {
  try {
    const db = await cacheDb();
    const saved = (await db.get("kv", CACHE_KEY)) as { scope: string; state: Parameters<typeof hydrate>[1]; at: number } | undefined;
    if (saved && saved.scope === scope && Date.now() - saved.at < 7 * 24 * 60 * 60_000) hydrate(qc, saved.state);
  } catch {
    /* ignore */
  }
}

export function persistCache(qc: QueryClient, scope: string) {
  let timer: ReturnType<typeof setTimeout> | null = null;
  const save = async () => {
    timer = null;
    try {
      const state = dehydrate(qc, {
        shouldDehydrateQuery: (q: Query) => q.state.status === "success" && PERSIST_ROOTS.has(String(q.queryKey[0])),
      });
      const db = await cacheDb();
      await db.put("kv", { scope, state, at: Date.now() }, CACHE_KEY);
    } catch {
      /* ignore */
    }
  };
  return qc.getQueryCache().subscribe(() => {
    if (!timer) timer = setTimeout(save, 1500);
  });
}
